/** generation_code_list 中在 generation_map 里存在的 code，顺序不变 */
function listCodesExistingInMap(card, generationMap) {
    return (card?.generation_code_list || []).filter((c) => generationMap[c]);
}
/** 是否不可选：can_select === 1；缺省 can_select 视为可选 */
function isGenerationNotSelectable(code, generationMap) {
    return generationMap[code]?.can_select === 1;
}
/** 列表中（均已存在于 map）是否全部不可选 */
function allCodesNotSelectable(codes, generationMap) {
    if (!codes.length)
        return true;
    return codes.every((c) => isGenerationNotSelectable(c, generationMap));
}
/** 同一张卡片内：按 default_generation_index 取默认 code，若不可选则取第一个可选；都不可选则取列表最后一个 */
export function resolveDefaultGenCodeForCard(card, generationMap) {
    const validCodes = listCodesExistingInMap(card, generationMap);
    if (!validCodes.length)
        return undefined;
    const rawIdx = card?.default_generation_index;
    const idx = typeof rawIdx === 'number' && rawIdx >= 0 && rawIdx < validCodes.length
        ? rawIdx
        : 0;
    const rawPick = validCodes[idx] ?? validCodes[0];
    if (!isGenerationNotSelectable(rawPick, generationMap)) {
        return rawPick;
    }
    const firstSelectable = validCodes.find((c) => !isGenerationNotSelectable(c, generationMap));
    if (firstSelectable)
        return firstSelectable;
    return validCodes[validCodes.length - 1];
}
/** 全表按卡片顺序、同卡内按 generation_code_list 顺序，找第一个可选车款 */
function firstSelectableCardInVersionList(vl, generationMap) {
    for (let i = 0; i < vl.length; i++) {
        const codes = listCodesExistingInMap(vl[i], generationMap);
        for (const code of codes) {
            if (!isGenerationNotSelectable(code, generationMap)) {
                return { cardIndex: i, code };
            }
        }
    }
    return null;
}
/**
 * 默认展开的版本卡片下标：
 * - 无 generationMap 时：仅 default_version_index，异常回退 0。
 * - 有 generationMap 时：若首张卡片 generation_code_list 在 map 中全部不可选，则改为「全表第一个可选车款」所在卡片；否则仍用 default_version_index（做边界裁剪）。
 */
export function resolveVerPageDefaultCardIndex(versionPageInfo, generationMap) {
    const vl = versionPageInfo.version_list;
    if (!vl?.length)
        return 0;
    const raw = versionPageInfo.default_version_index;
    const boundedFromDefault = () => {
        if (typeof raw !== 'number' || raw < 0 || raw >= vl.length)
            return 0;
        return raw;
    };
    if (!generationMap) {
        return boundedFromDefault();
    }
    const firstCardCodes = listCodesExistingInMap(vl[0], generationMap);
    if (allCodesNotSelectable(firstCardCodes, generationMap)) {
        const hit = firstSelectableCardInVersionList(vl, generationMap);
        if (hit)
            return hit.cardIndex;
    }
    return boundedFromDefault();
}
/**
 * 默认车款 code：
 * - 卡片下标由 resolveVerPageDefaultCardIndex(info, generationMap) 决定（含首张全不可选时的纠正）。
 * - 车款由 resolveDefaultGenCodeForCard：默认 index 对应不可选则同卡第一个可选；全不可选则该卡列表最后一个。
 * - 当前卡无有效 code 时依次尝试其它卡片。
 */
export function resolveVerPageDefaultGenCode(versionPageInfo, generationMap) {
    const vl = versionPageInfo.version_list;
    if (!vl?.length || !generationMap)
        return undefined;
    const cardIdx = resolveVerPageDefaultCardIndex(versionPageInfo, generationMap);
    const tryCard = (i) => resolveDefaultGenCodeForCard(vl[i], generationMap);
    let code = tryCard(cardIdx);
    if (code)
        return code;
    for (let i = 0; i < vl.length; i++) {
        if (i === cardIdx)
            continue;
        code = tryCard(i);
        if (code)
            return code;
    }
    return undefined;
}
